/**
 * PARAFON ÖNCESİ KAYIT DÖKÜMÜ — PDF HÂLİ (geri bildirim 8TVG-AEZC,
 * 26.10.2026). CSV'si `parafonOncesi.ts`te okunan tablonun (Hesap, Fon adı,
 * Tarih, ± Alınan / Satılan, Birim Fiyat, Masraf, Açıklama, Dengeleme, TCMB
 * kuru) yazdırılmış hâli. Tablo burada AYNI sütunlarla CSV metnine çevrilir
 * ve CSV okuyucusuna verilir: kurallar tek kopya.
 *
 * PDF'e özgü iki durum (kullanıcının maskeli dosyasında görüldü):
 *  - Türkçe harfler ayrı parça yazılır ("Hay" "ı" "r"): aralarında boşluk
 *    yoksa (`BITISIK`) aynı kelimedir.
 *  - Uzun sayı hücresi alt satıra taşar ("123,456789" / "1234"; işaret tek
 *    başına üstte: "-" / "9,87654321"): bir satırın devamı, sıradaki satırın
 *    başına kadar aynı sütunda alt alta duran parçalardır; sayılar boşluksuz,
 *    metinler boşlukla birleşir.
 *
 * Hücreler CSV'dekiyle birebir aynı metin olur (",05984" de: CSV okuyucusu
 * onu zaten okuyor) — aynı kayıt CSV'den ve PDF'ten aynı kalıcı kimliği alır.
 *
 * Sütunlar başlıktan: ilk başlık satırındaki hücrelerin yatay aralıkları; alt
 * başlık satırları ("Satılan", "($)", "Dolar Kuru") üst üste binen hücreye
 * eklenir. Veri parçası en çok örtüştüğü (yoksa en yakın) sütuna gider.
 */

import { csvYaz, sade } from "./iceAktarma";
import type { MetinParcasi } from "./ekstreMaske";
import { satirlar, type MidasBelgesi, type Satir } from "./midasEkstre";

/** Bu kadar puntodan dar aralık aynı kelime sayılır (ayrı yazılan Türkçe harf). */
const BITISIK = 1;

interface Sutun {
  baslik: string;
  x0: number;
  x1: number;
}

/** Sayı sütunları: devam satırı boşluksuz eklenir. */
const SAYI_SUTUNLARI = ["alinan satilan", "birim fiyat", "masraf", "tcmb dolar kuru"];

const TARIH = /^\d{2}\.\d{2}\.\d{4}$/;

function sayiSutunuMu(baslik: string): boolean {
  const s = sade(baslik.replace(/\([^)]*\)\s*$/, ""));
  return SAYI_SUTUNLARI.includes(s);
}

/** Aynı satırdaki parçaları birleştirir: bitişikse boşluksuz, değilse boşlukla. */
function birlestir(parcalar: readonly MetinParcasi[]): string {
  let out = "";
  let sonX: number | null = null;
  for (const p of [...parcalar].sort((a, b) => a.x - b.x)) {
    const t = p.metin.trim();
    if (t === "") continue;
    out += sonX === null || p.x - sonX < BITISIK ? t : ` ${t}`;
    sonX = p.x + p.genislik;
  }
  return out;
}

/** Bir satırı, aralarında boşluk olan hücre öbeklerine ayırır (başlık için). */
function obekler(s: Satir): MetinParcasi[][] {
  const out: MetinParcasi[][] = [];
  let sonX: number | null = null;
  for (const p of [...s].sort((a, b) => a.x - b.x)) {
    const son = out[out.length - 1];
    // Başlıktaki sütun aralıkları ≥ 10 punto; kelimeler arası boşluk bir parçanın içinde.
    if (son !== undefined && sonX !== null && p.x - sonX < 4) son.push(p);
    else out.push([p]);
    sonX = p.x + p.genislik;
  }
  return out;
}

const ortusme = (a0: number, a1: number, b0: number, b1: number): number => Math.min(a1, b1) - Math.max(a0, b0);

function sutunBul(sutunlar: readonly Sutun[], x0: number, x1: number): number {
  let en = 0;
  let enIyi = Number.NEGATIVE_INFINITY;
  sutunlar.forEach((c, i) => {
    // Örtüşme artı, uzaklık eksi: örtüşen yoksa en yakın aralık.
    const o = ortusme(c.x0, c.x1, x0, x1);
    if (o > enIyi) {
      enIyi = o;
      en = i;
    }
  });
  return en;
}

function baslikSatiriMi(s: Satir): boolean {
  const t = sade(birlestir(s));
  return /\bhesap\b/.test(t) && /\btarih\b/.test(t) && /\bbirim fiyat\b/.test(t);
}

/** Başlık bloğu → sütunlar. İlk satırın öbekleri sütunları belirler. */
function sutunlariKur(blok: readonly Satir[]): Sutun[] {
  const [ilk, ...diger] = blok;
  if (ilk === undefined) return [];
  const sutunlar: (Sutun & { parcalar: string[] })[] = obekler(ilk).map((o) => ({
    baslik: "",
    x0: Math.min(...o.map((p) => p.x)),
    x1: Math.max(...o.map((p) => p.x + p.genislik)),
    parcalar: [birlestir(o)],
  }));
  for (const s of diger) {
    for (const o of obekler(s)) {
      const x0 = Math.min(...o.map((p) => p.x));
      const x1 = Math.max(...o.map((p) => p.x + p.genislik));
      const c = sutunlar[sutunBul(sutunlar, x0, x1)];
      if (c === undefined) continue;
      c.parcalar.push(birlestir(o));
      c.x0 = Math.min(c.x0, x0);
      c.x1 = Math.max(c.x1, x1);
    }
  }
  return sutunlar.map((c) => ({ baslik: c.parcalar.join(" "), x0: c.x0, x1: c.x1 }));
}

export interface ParafonOncesiPdf {
  /** CSV okuyucusuna verilecek metin (`;`, Türkçe sayı). */
  readonly metin: string;
  readonly satirSayisi: number;
}

/**
 * Belgeler bu tablodaysa CSV metni; değilse `null`. Her sayfada başlık
 * yeniden aranır; bütün sayfaların başlığı aynı olmalı.
 */
export function parafonOncesiPdf(belgeler: readonly MidasBelgesi[]): ParafonOncesiPdf | null {
  let baslik: string[] | null = null;
  const satirlarCsv: string[][] = [];
  for (const b of belgeler) {
    for (const sayfa of b.sayfalar) {
      const sat = satirlar(sayfa.parcalar);
      const bi = sat.findIndex(baslikSatiriMi);
      if (bi < 0) return null;
      // Başlık bloğu: ilk veri satırına (tarih sütununda tarih) kadar.
      let sutunlar = sutunlariKur([sat[bi] as Satir]);
      const tarihSutunu = (): number => sutunlar.findIndex((c) => sade(c.baslik) === "tarih");
      const veriMi = (s: Satir): boolean => {
        const t = tarihSutunu();
        return t >= 0 && s.some((p) => TARIH.test(p.metin.trim()) && sutunBul(sutunlar, p.x, p.x + p.genislik) === t);
      };
      let vi = bi + 1;
      while (vi < sat.length && !veriMi(sat[vi] as Satir)) vi += 1;
      sutunlar = sutunlariKur(sat.slice(bi, vi));
      const bu = sutunlar.map((c) => c.baslik);
      if (baslik === null) baslik = bu;
      else if (bu.join("|") !== baslik.join("|")) return null;
      const sayiMi = sutunlar.map((c) => sayiSutunuMu(c.baslik));

      let hucre: string[][] | null = null;
      let sonY = 0;
      const bitir = (): void => {
        if (hucre === null) return;
        satirlarCsv.push(hucre.map((h, i) => (sayiMi[i] ? h.join("") : h.join(" "))));
        hucre = null;
      };
      for (const s of sat.slice(vi)) {
        const y = s[0]?.y ?? 0;
        const boy = Math.max(...s.map((p) => p.boy), 1);
        if (veriMi(s)) {
          bitir();
          hucre = sutunlar.map(() => []);
        } else if (hucre === null || y - sonY > boy * 2) {
          // Sayfa numarası, dipnot: bir satırın devamı değil.
          bitir();
          continue;
        }
        const yeni = sutunlar.map(() => [] as MetinParcasi[]);
        for (const p of s) yeni[sutunBul(sutunlar, p.x, p.x + p.genislik)]?.push(p);
        yeni.forEach((ps, i) => {
          const t = birlestir(ps);
          if (t !== "") (hucre as string[][])[i]?.push(t);
        });
        sonY = y;
      }
      bitir();
    }
  }
  if (baslik === null || satirlarCsv.length === 0) return null;
  return { metin: csvYaz([baslik, ...satirlarCsv], ";"), satirSayisi: satirlarCsv.length };
}
